/**
 * Resolve SLA label for Request / Approvals desk cards from received time + policy.
 * Text-only tones: overdue (danger), due soon (warning), on track (success).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function parseTimeMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function endOfDayMs(value) {
  const ms = parseTimeMs(value);
  if (ms == null) return null;
  const d = new Date(ms);
  if (String(value).length <= 10 && !String(value).includes("T")) {
    d.setHours(23, 59, 59, 999);
  }
  return d.getTime();
}

function resolveWorkflowSlaDays(request) {
  const snap = request?.workflowSnapshot || {};
  const steps =
    (Array.isArray(snap.approvalSteps) && snap.approvalSteps.length
      ? snap.approvalSteps
      : null) ||
    (Array.isArray(snap.steps) && snap.steps.length ? snap.steps : null) ||
    (Array.isArray(request?.workflowSteps) ? request.workflowSteps : null) ||
    [];

  if (!steps.length) {
    const explicit = Number(request?.slaDays ?? request?.formData?.slaDays);
    return Number.isFinite(explicit) && explicit > 0 ? explicit : 3;
  }

  const current = Number(request?.currentStep);
  const match =
    (Number.isFinite(current) && current > 0
      ? steps.find((s) => Number(s.order ?? s.stepOrder) === current)
      : null) ||
    steps.find((s) => {
      const st = String(s.status || "").toLowerCase();
      return st === "current" || st === "in_progress" || st === "pending";
    }) ||
    steps[Math.max(0, (Number.isFinite(current) ? current : 1) - 1)] ||
    steps[0];

  const days = Number(match?.slaDays);
  return Number.isFinite(days) && days > 0 ? days : 3;
}

/** Clock starts when the current step became pending (last prior action) or when received. */
function resolveSlaStartMs(request) {
  const approvals = Array.isArray(request?.approvals) ? request.approvals : [];
  const acted = approvals
    .map((a) => parseTimeMs(a.actedAt || a.approvedAt || a.updatedAt))
    .filter((ms) => ms != null)
    .sort((a, b) => b - a);
  if (acted.length) return acted[0];

  const trail = Array.isArray(request?.workflowTrail)
    ? request.workflowTrail
    : Array.isArray(request?.workflowSteps)
      ? request.workflowSteps
      : [];
  const trailActed = trail
    .map((s) => parseTimeMs(s.actedAt))
    .filter((ms) => ms != null)
    .sort((a, b) => b - a);
  if (trailActed.length) return trailActed[0];

  return (
    parseTimeMs(request?.submittedAt) ||
    parseTimeMs(request?.createdAt) ||
    parseTimeMs(request?.updatedAt) ||
    Date.now()
  );
}

/**
 * @returns {{ key: string, label: string, className: string } | null}
 */
export function resolveDeskSlaStatus(request, nowMs = Date.now()) {
  if (!request) return null;
  const status = String(request.status || "").toLowerCase();
  if (status && status !== "pending") return null;
  if (request.readOnlyApproval && status !== "pending") return null;
  if (request.notifyOnlyFyi) return null;

  if (request.formData?.slaPending) {
    return {
      key: "waiting",
      label: "SLA…",
      title: "SLA pending — waiting for profile / trigger before SLA starts",
      className: "text-[hsl(var(--ds-muted-fg))]",
    };
  }

  const dueRaw = request.formData?.dueAt || request.dueAt || null;
  let dueMs = dueRaw ? endOfDayMs(dueRaw) : null;

  if (dueMs == null) {
    const slaDays = resolveWorkflowSlaDays(request);
    const startMs = resolveSlaStartMs(request);
    dueMs = startMs + slaDays * DAY_MS;
  }

  if (dueMs == null || !Number.isFinite(dueMs)) return null;

  const remaining = dueMs - nowMs;
  if (remaining < 0) {
    return {
      key: "overdue",
      label: "Overdue",
      className: "text-[hsl(var(--ds-primary))]",
    };
  }
  if (remaining <= DAY_MS) {
    return {
      key: "due_soon",
      label: "Due soon",
      className: "text-[hsl(var(--ds-warning-fg))]",
    };
  }
  return {
    key: "on_track",
    label: "On track",
    className: "text-[hsl(var(--ds-success))]",
  };
}
